import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { kindLabel } from '../lib/driverKind';
import { api, type CommuteHeld, type CommuteLine, type CommuteMonth, type CommuteOverview, type CommuteReloadDiff, type CommuteRouteRequest, type CarAlloc } from '../lib/api';
import { ErrorBox, commaInput, dash, money, moneyInput, uncomma, MoneyInput } from '../components/DataTable';
import Modal from '../components/Modal';
import { FitBox } from '../components/FitBox';
import { isVatIncl, VatPreview, VatToggle } from '../components/Vat';
import { canScreen, useMe } from '../lib/me';

/**
 * 통근 운행일수 — 게시판 #51(일 단위 운행료 조정)·#52(정산할 기사 등록·삭제), 2026-09-30.
 *
 * 통근은 **달마다 정산 줄을 만들고**(노선 × 월 청구 줄 + 배정 × 월 운행료 줄) 줄마다 **운행일수**를 적는다.
 * 운행료 = 1대당 단가 × 일수. 레거시 버스인의 통근 정산 흐름을 그대로 옮겼다(`functions/src/commuteMonth.ts`).
 * 여기서 적은 값이 거래처별 정산 상세·기사 급여명세(명세 다시 만들기)에 그대로 들어간다.
 *
 * 🔴 마감 판정은 서버가 한다(일수·단가 = 기사 마감 · 줄 만들기·기사 추가/빼기 = 3축 마감) — 막히면 그 문구를 그대로 보인다.
 */
export default function SettleCommute({ driveMon }: { driveMon: string }) {
  const { me } = useMe();
  const canWrite = canScreen(me, ['settle']);
  const [data, setData] = useState<CommuteOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [openRecv, setOpenRecv] = useState<number | null>(null);
  const [q, setQ] = useState('');
  const [bulk, setBulk] = useState<{ routes: number; lines: number } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!driveMon) return;
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .commuteMonthOverview({ driveMon })
      .then((r) => alive && setData(r))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : '불러오지 못했습니다.'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [driveMon, nonce]);

  useEffect(() => { setOpenRecv(null); }, [driveMon]);

  const rows = useMemo(() => {
    const t = q.trim();
    const list = data?.rows ?? [];
    return t ? list.filter((r) => `${r.cust_name ?? ''} ${r.recv_id}`.includes(t)) : list;
  }, [data, q]);
  const prevMissing = (data?.rows ?? []).reduce((s, r) => s + r.prev_missing, 0);

  async function previewBulk() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.createCommuteMonth({ driveMon, dryRun: true });
      setBulk({ routes: r.routes, lines: r.lines });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '미리보기를 못 했습니다.');
    } finally {
      setBusy(false);
    }
  }
  /** 새 노선 결재 스위치(관리자) — 켜면 지난달에 없던 노선은 DYOPS 결재 뒤에만 정산 줄이 생긴다. */
  async function flipGate() {
    const on = !data?.gate;
    if (!window.confirm(on
      ? '새 노선 결재를 켤까요? 지난달 정산에 없던 노선은 담당자가 결재를 올려 승인받아야 정산 줄이 만들어집니다(자동 반영 포함). 이미 만든 줄은 그대로입니다.'
      : '새 노선 결재를 끌까요? 지금처럼 고르면 바로 만들어집니다. 이미 올린 결재는 끝까지 처리됩니다.')) return;
    setBusy(true);
    setError(null);
    try {
      await api.setCommuteRouteGate({ on });
      setNonce((n) => n + 1);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '바꾸지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }
  async function runBulk() {
    setBusy(true);
    setError(null);
    try {
      await api.createCommuteMonth({ driveMon });
      setBulk(null);
      setNonce((n) => n + 1);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '만들지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  if (!driveMon) return <p className="text-sm text-ink-soft">정산년월을 고르세요.</p>;

  return (
    <div className="space-y-1.5">
      <div className="card flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
        <div className="break-keep text-ink-soft">
          통근은 달마다 <b>정산 줄</b>을 만들고 기사마다 <b>운행일수</b>를 적습니다. 운행료 = 1대당 단가 × 일수.
          {data && data.closed.length > 0 && (
            <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700">
              {driveMon} {data.closed.join('·')} 마감
            </span>
          )}
        </div>
        {data && (me?.role === 'admin' ? (
          <button className={`rounded px-2 py-0.5 text-xs font-medium ${data.gate ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-ink-soft'}`}
            disabled={busy} onClick={() => void flipGate()} title="켜면 지난달 정산에 없던 노선은 DYOPS 결재 뒤에만 정산 줄이 만들어집니다">
            새 노선 결재 {data.gate ? '켜짐' : '꺼짐'}
          </button>
        ) : data.gate && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800" title="지난달 정산에 없던 노선은 결재 뒤에만 들어갑니다">
            새 노선 결재 중
          </span>
        ))}
        <input className="field ml-auto w-48" placeholder="거래처 · 접수 NO" value={q} onChange={(e) => setQ(e.target.value)} />
        {canWrite && prevMissing > 0 && (
          <button className="btn-primary" disabled={busy} onClick={() => void previewBulk()}>
            지난달({data?.prevMon}) 노선으로 이 달 만들기
          </button>
        )}
      </div>

      {error && <ErrorBox message={error} />}

      {bulk && (
        <Modal>
          <BulkConfirm
            driveMon={driveMon}
            prevMon={data?.prevMon}
            bulk={bulk}
            busy={busy}
            onCancel={() => setBulk(null)}
            onRun={() => void runBulk()}
          />
        </Modal>
      )}

      {openRecv !== null && (
        <CommuteEditor
          recvId={openRecv}
          driveMon={driveMon}
          canWrite={canWrite}
          onClose={() => setOpenRecv(null)}
          onChanged={() => setNonce((n) => n + 1)}
        />
      )}

      <FitBox deps={[rows.length, loading, openRecv, bulk, error]} className="rounded-lg border border-line">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="th text-right">접수 NO</th>
              <th className="th">거래처</th>
              <th className="th text-right">지난달 노선</th>
              <th className="th text-right">이 달 노선</th>
              <th className="th text-right" title="이 달 노선 중 지난달 정산에 없던 노선">새로 생김</th>
              <th className="th text-right" title="지난달엔 만들었는데 이 달엔 아직 없는 노선">지난달만 있음</th>
              {data?.gate && <th className="th text-right" title="결재를 올렸고 아직 정산 줄이 안 생긴 새 노선">결재 대기</th>}
              <th className="th text-right">기사 줄</th>
              <th className="th text-right">일수 입력</th>
              <th className="th text-right">운행료 합</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.recv_id}
                className={`cursor-pointer hover:bg-canvas ${openRecv === r.recv_id ? 'bg-brand-50' : ''}`}
                onClick={() => setOpenRecv(r.recv_id)}
              >
                <td className="td num text-xs text-ink-soft">{r.recv_id}</td>
                <td className="td">{dash(r.cust_name)}</td>
                <td className="td num text-ink-soft">{r.prev_cnt || '—'}</td>
                <td className="td num">{r.made_cnt}</td>
                <td className={`td num ${r.new_cnt > 0 ? 'font-semibold text-sky-700' : 'text-ink-faint'}`}>{r.new_cnt || '—'}</td>
                <td className={`td num ${r.prev_missing > 0 ? 'font-semibold text-amber-700' : 'text-ink-faint'}`}>{r.prev_missing || '—'}</td>
                {data?.gate && <td className={`td num ${r.pending_cnt > 0 ? 'font-semibold text-amber-700' : 'text-ink-faint'}`}>{r.pending_cnt || '—'}</td>}
                <td className="td num">{r.line_cnt}</td>
                <td className={`td num ${r.line_cnt > 0 && r.filled_cnt < r.line_cnt ? 'text-amber-700' : ''}`}>
                  {r.line_cnt ? `${r.filled_cnt} / ${r.line_cnt}` : '—'}
                </td>
                <td className="td num">{money(r.driver_amt)}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td className="td text-ink-faint" colSpan={data?.gate ? 10 : 9}>이 달에 정산할 통근 접수가 없습니다.</td>
              </tr>
            )}
            {loading && (
              <tr>
                <td className="td text-ink-faint" colSpan={data?.gate ? 10 : 9}>불러오는 중…</td>
              </tr>
            )}
          </tbody>
        </table>
      </FitBox>
    </div>
  );
}

/** 지난달 노선으로 이 달 줄 만들기 — 확인 창(건수는 dryRun 결과). */
function BulkConfirm({ driveMon, prevMon, bulk, busy, onCancel, onRun }: {
  driveMon: string;
  prevMon: string | undefined;
  bulk: { routes: number; lines: number };
  busy: boolean;
  onCancel: () => void;
  onRun: () => void;
}) {
  return (
    <div className="card space-y-4 p-4">
      <h2 className="text-lg font-bold">{driveMon} 통근 정산 줄 만들기</h2>
      <p className="break-keep text-sm text-ink-soft">
        {prevMon} 에 만든 노선으로 이 달 줄을 만듭니다. 이미 있는 줄은 건너뜁니다.
        <br />
        노선 <b>{bulk.routes.toLocaleString()}</b>건 · 기사(배정) <b>{bulk.lines.toLocaleString()}</b>줄이 새로 생깁니다.
        기사 줄은 이 달에 운행기간이 걸친 배정만 들어가고, 일수는 비어 있습니다.
      </p>
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" disabled={busy} onClick={onCancel}>취소</button>
        <button className="btn-primary" disabled={busy || bulk.routes + bulk.lines === 0} onClick={onRun}>
          {busy ? '만드는 중…' : '만들기'}
        </button>
      </div>
    </div>
  );
}

/** 접수 하나의 이 달 통근 정산 — 노선 만들기 · 기사별 일수·단가 · 기사 추가/빼기 · 노선 청구 금액. */
export function CommuteEditor({
  recvId,
  driveMon,
  canWrite,
  openRoute,
  openRouteIncl,
  onClose,
  onChanged,
  onReq,
}: {
  recvId: number;
  driveMon: string;
  canWrite: boolean;
  /** #326 — 기사 줄에서 바로 영업정산 수당·공제(거래처 청구 상세가 넘긴다 · 이 창을 닫고 그 기사 범위로 등록 창을 연다). */
  onReq?: (line: CommuteLine) => void;
  /** 열자마자 띄울 노선 청구 금액 창(정산 탭 청구액 칸에서 열었을 때, #70). */
  openRoute?: number | null;
  /** 그 창을 「부가세 포함」으로 연다(청구액(포함) 칸에서 열었을 때, #247). */
  openRouteIncl?: boolean;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const [m, setM] = useState<CommuteMonth | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState(false);
  /** 고친 칸 — id → { 일수, 단가 }. 저장 버튼 한 번에 보낸다. */
  const [edits, setEdits] = useState<Record<number, { cnt: string; cost: string }>>({});
  const [pick, setPick] = useState<Set<number>>(new Set());
  const [addFor, setAddFor] = useState<number | null>(null);
  const [routeFor, setRouteFor] = useState<number | null>(null);
  const [routeRemove, setRouteRemove] = useState(false); // #297 기사를 다 뺀 노선 줄에서 바로 「이 달 정산에서 빼기」
  const [reloadOpen, setReloadOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const routeOpened = useRef(false);
  /** 찾기(#81 — 버스인 기사운행료등록의 Find). 노선명·기사·차량번호·차주로 거르고 합계도 거른 줄만 더한다. */
  const [find, setFind] = useState('');

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .getCommuteMonth({ recvId, driveMon })
      .then((r) => {
        if (!alive) return;
        setM(r);
        // 🔴 저장 전 입력은 다시 불러와도 남긴다(#69 — 기사 추가·빼기가 다시 불러오며 적어 둔 일수를 지웠다).
        //    뺀 줄의 입력만 버린다. 비우는 건 「일수·단가 저장」이 성공했을 때뿐이다.
        const live = new Set(r.lines.map((l) => l.id));
        setEdits((cur) => Object.fromEntries(Object.entries(cur).filter(([id]) => live.has(Number(id)))));
        if (!routeOpened.current && openRoute != null && r.routes.some((x) => x.dispt_id === openRoute && x.made)) {
          routeOpened.current = true;
          setRouteFor(openRoute);
        }
        // 만들 노선 기본 체크 = 지난달에 만든 노선(레거시 담당자가 매달 고르던 것 — 실측 92%가 지난달과 같다).
        // 이 달에 뺐던 노선·이름 날짜가 다른 달인 노선은 체크하지 않는다(자동 반영도 그 둘은 안 넣는다).
        setPick(new Set(r.routes.filter((x) => !x.made && x.made_prev && !x.removed && !x.name_other).map((x) => x.dispt_id)));
      })
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : '불러오지 못했습니다.'));
    return () => { alive = false; };
  }, [recvId, driveMon, nonce]);

  const reload = () => { setNonce((n) => n + 1); onChanged?.(); };
  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '처리하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  const linesBy = useMemo(() => {
    const map = new Map<number, CommuteLine[]>();
    for (const l of m?.lines ?? []) map.set(l.dispt_id, [...(map.get(l.dispt_id) ?? []), l]);
    return map;
  }, [m]);
  const dirty = Object.keys(edits).length;
  const cellCnt = (l: CommuteLine) => edits[l.id]?.cnt ?? (l.cost_driver_cnt === null ? '' : String(l.cost_driver_cnt));
  const cellCost = (l: CommuteLine) => edits[l.id]?.cost ?? moneyInput(l.cost_driver);
  const setCell = (l: CommuteLine, k: 'cnt' | 'cost', v: string) =>
    setEdits((cur) => ({ ...cur, [l.id]: { cnt: cellCnt(l), cost: cellCost(l), ...cur[l.id], [k]: v } }));
  const amountOf = (l: CommuteLine) => {
    const c = Number(uncomma(cellCost(l)) || 0);
    const n = Number(cellCnt(l) || 0);
    return c * n;
  };
  const total = (m?.lines ?? []).reduce((s, l) => s + amountOf(l), 0);

  function saveLines() {
    const items = Object.entries(edits).map(([id, v]) => ({
      id: Number(id),
      cnt: v.cnt.trim() === '' ? null : Number(v.cnt),
      costDriver: v.cost.trim() === '' ? null : uncomma(v.cost),
    }));
    void act(async () => {
      await api.saveCommuteLines({ items });
      setEdits({});
    });
  }
  function close() {
    if (dirty && !window.confirm(`저장하지 않은 일수·단가가 ${dirty}줄 있습니다. 저장하지 않고 닫을까요?`)) return;
    onClose();
  }

  const notMade = (m?.routes ?? []).filter((r) => !r.made);
  // 새 노선 승인(미팅 #30) — 결재가 필요한 노선은 「만들기」 대신 「결재 올리기」로 간다. 이미 대기·승인 중이면 다시 못 올린다.
  const needOk = new Set(notMade.filter((r) => m?.gate && r.need_ok).map((r) => r.dispt_id));
  const waiting = new Set(notMade.filter((r) => r.req_status === '요청' || r.req_status === '승인').map((r) => r.dispt_id));
  const pickMake = [...pick].filter((id) => !needOk.has(id));
  const pickAsk = [...pick].filter((id) => needOk.has(id) && !waiting.has(id));
  const allMade = (m?.routes ?? []).filter((r) => r.made);
  const needle = find.trim().toLowerCase().replace(/\s+/g, '');
  const hit = (v: unknown) => String(v ?? '').toLowerCase().replace(/\s+/g, '').includes(needle);
  const shownLines = (dispt: number, name: unknown) => {
    const ls = linesBy.get(dispt) ?? [];
    if (!needle || hit(name)) return ls;
    return ls.filter((l) => hit(l.driver_name) || hit(l.car_no) || hit(l.owner_name));
  };
  const made = needle ? allMade.filter((r) => shownLines(r.dispt_id, r.dispt_name).length > 0 || hit(r.dispt_name)) : allMade;
  const shownAll = made.flatMap((r) => shownLines(r.dispt_id, r.dispt_name));
  const shownTotal = shownAll.reduce((a, l) => a + amountOf(l), 0);
  const shownDays = shownAll.reduce((a, l) => a + Number(cellCnt(l) || 0), 0);

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm font-bold">
          {driveMon} · {dash(m?.custName)}
          <span className="ml-2 font-normal text-ink-soft">접수 NO {recvId} · 노선 {allMade.length}건 · 기사 {m?.lines.length ?? 0}줄</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {canWrite && (
            <>
              <button className="btn-ghost py-1 text-xs" disabled={busy} onClick={() => setReloadOpen(true)}
                title="지금 접수·배차의 배정 기간과 비교해 빠진 노선·기사를 넣고, 없어진 것을 뺍니다(고른 것만)">
                배차에서 다시 불러오기
              </button>
              <button className="btn-ghost py-1 text-xs" disabled={busy} onClick={() => setCopyOpen(true)}
                title="고른 달의 노선·기사·단가·운행수를 그대로 이 달로 옮깁니다">
                다른 달 그대로 가져오기
              </button>
            </>
          )}
          {canWrite && (
            <button className="btn-primary py-1 text-sm" disabled={busy || dirty === 0} onClick={saveLines}>
              {busy ? '저장 중…' : dirty ? `일수·단가 저장 (${dirty}줄)` : '일수·단가 저장'}
            </button>
          )}
          <button className="btn-ghost" onClick={close}>닫기</button>
        </div>
      </div>

      {error && <ErrorBox message={error} />}
      {!m && !error && <p className="text-sm text-ink-faint">불러오는 중…</p>}

      {m && canWrite && notMade.length > 0 && (
        <div className="rounded-lg border border-line p-3 text-sm">
          <div className="mb-2 flex items-center gap-2">
            <b>{m.auto ? '자동으로 안 들어간 노선' : '이 달 아직 안 만든 노선'}</b>
            <span className="text-xs text-ink-soft">
              {m.auto
                ? '이 달에 걸친 배정이 있지만 지난달 정산에 없었거나, 이 달에 뺐거나, 이름 날짜가 다른 달인 노선입니다.'
                : '지난달에 만든 노선은 미리 체크돼 있습니다.'}
              {m.gate && ' 지난달 정산에 없던 노선은 결재(DYOPS 의사결정)를 받은 뒤에 들어갑니다.'}
            </span>
            <div className="ml-auto flex gap-2">
              {pickAsk.length > 0 && (
                <button className="btn-ghost py-1 text-xs text-amber-800" disabled={busy} onClick={() => setAskOpen(true)}
                  title="지난달 정산에 없던 노선 — 팀장 결재를 받은 뒤에 정산 줄이 만들어집니다">
                  결재 올리기 ({pickAsk.length})
                </button>
              )}
              <button
                className="btn-primary py-1 text-xs"
                disabled={busy || pickMake.length === 0}
                onClick={() => void act(() => api.createCommuteMonth({ driveMon, recvId, disptIds: pickMake }))}
              >
                고른 노선 만들기 ({pickMake.length})
              </button>
            </div>
          </div>
          <div className="grid gap-1 sm:grid-cols-2">
            {notMade.map((r) => (
              <label key={r.dispt_id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={pick.has(r.dispt_id)}
                  onChange={() =>
                    setPick((cur) => {
                      const n = new Set(cur);
                      if (n.has(r.dispt_id)) n.delete(r.dispt_id);
                      else n.add(r.dispt_id);
                      return n;
                    })
                  }
                />
                <span>{dash(r.dispt_name)}</span>
                <span className="text-xs text-ink-faint">
                  {r.made_prev ? '지난달 있음' : '지난달 없음'} · 이 달 배정 {r.cover_cnt}대
                </span>
                {m.gate && r.need_ok && (
                  <span className={`rounded px-1.5 text-xs ${r.req_status === '반려' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                    {r.req_status === '요청' ? '결재 대기' : r.req_status === '승인' ? '승인 · 반영 대기' : r.req_status === '반려' ? '반려됨' : '새 노선 · 결재 필요'}
                  </span>
                )}
                {r.removed && <span className="rounded bg-amber-100 px-1.5 text-xs text-amber-800">이 달에 뺀 노선</span>}
                {r.name_other && <span className="rounded bg-red-100 px-1.5 text-xs text-red-700">이름 날짜가 다른 달</span>}
                {Number(r.cost_cust ?? 0) > 0 && r.cost_cust_cnt === 0 && (
                  <span className="rounded bg-amber-100 px-1.5 text-xs text-amber-800" title="노선의 청구횟수가 0이라 만들면 청구 0원입니다. 만든 뒤 청구를 눌러 횟수를 넣으세요.">청구 0회</span>
                )}
              </label>
            ))}
          </div>
        </div>
      )}

      {m && m.requests.length > 0 && (
        <RouteRequests recvId={recvId} driveMon={driveMon} requests={m.requests} canWrite={canWrite} onChanged={reload} />
      )}
      {askOpen && m && (
        <Modal>
          <RouteAsk
            recvId={recvId}
            driveMon={driveMon}
            routes={notMade.filter((r) => pickAsk.includes(r.dispt_id))}
            onClose={() => setAskOpen(false)}
            onDone={() => { setAskOpen(false); reload(); }}
          />
        </Modal>
      )}

      {m && allMade.length > 0 && (
        <div className="flex items-center gap-2">
          <input
            className="field w-64 py-1 text-sm"
            placeholder="찾기 — 기사·차량번호·노선·차주"
            value={find}
            onChange={(e) => setFind(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setFind(''); }}
          />
          {needle && (
            <>
              <span className="text-xs text-ink-soft">노선 {made.length}건 · 기사 {shownAll.length}줄</span>
              <button className="btn-ghost px-2 py-0.5 text-xs" onClick={() => setFind('')}>지우기</button>
            </>
          )}
        </div>
      )}
      {m && needle && made.length === 0 && <p className="text-sm text-ink-faint">「{find}」에 맞는 노선·기사가 없습니다.</p>}
      {m && made.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-line">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="th">노선 · 기사</th>
                <th className="th">차량</th>
                <th className="th">차주 · 소속</th>
                <th className="th text-right">운행료 단가</th>
                <th className="th text-right">일수</th>
                <th className="th text-right">운행료</th>
                <th className="th" />
              </tr>
            </thead>
            <tbody>
              {made.map((r) => {
                const lines = shownLines(r.dispt_id, r.dispt_name);
                return [
                  <tr key={`r${r.dispt_id}`} className="bg-canvas">
                    <td className="td font-semibold" colSpan={3}>
                      {dash(r.dispt_name)}
                      {canWrite ? (
                        <button
                          type="button"
                          className="ml-2 text-xs font-normal text-ink-soft underline decoration-ink-faint/50 decoration-dotted underline-offset-4 hover:text-brand-700"
                          title="눌러서 이 달 청구 단가·횟수 수정"
                          disabled={busy}
                          onClick={() => setRouteFor(r.dispt_id)}
                        >
                          청구 {money(r.cost_cust)} × {r.cost_cust_cnt ?? 1}회
                        </button>
                      ) : (
                        <span className="ml-2 text-xs font-normal text-ink-soft">
                          청구 {money(r.cost_cust)} × {r.cost_cust_cnt ?? 1}회
                        </span>
                      )}
                      {/* 노선 값의 청구횟수가 0 인 노선은 달 줄도 0회로 만들어진다 — 청구가 0원이 된다(#70). */}
                      {Number(r.cost_cust ?? 0) > 0 && r.cost_cust_cnt === 0 && (
                        <span className="ml-2 rounded bg-amber-100 px-1.5 text-xs font-normal text-amber-800">청구 0회 — 횟수 확인</span>
                      )}
                    </td>
                    {/* 노선 합계(#115) — 고치는 중인 값으로 바로 다시 센다. */}
                    {lines.length > 0 ? (
                      <>
                        <td className="td num font-semibold">{money(String(lines.reduce((a, l) => a + Number(uncomma(cellCost(l)) || 0), 0)))}</td>
                        <td className="td num font-semibold">{lines.reduce((a, l) => a + Number(cellCnt(l) || 0), 0).toLocaleString()}</td>
                        <td className="td num font-semibold">{money(String(lines.reduce((a, l) => a + amountOf(l), 0)))}</td>
                      </>
                    ) : (
                      <td className="td" colSpan={3} />
                    )}
                    <td className="td whitespace-nowrap text-right">
                      {canWrite && (
                        <>
                          <button className="btn-ghost px-1.5 py-0 text-xs" disabled={busy} onClick={() => setAddFor(r.dispt_id)}>기사 추가</button>
                          <button className="btn-ghost px-1.5 py-0 text-xs" disabled={busy} onClick={() => setRouteFor(r.dispt_id)}>노선</button>
                        </>
                      )}
                    </td>
                  </tr>,
                  ...lines.map((l) => (
                    <tr key={l.id}>
                      <td className="td pl-6">
                        {dash(l.driver_name)}
                        <span className="ml-1 text-xs text-ink-faint">{dash(kindLabel(l.driver_kind))}</span>
                      </td>
                      <td className="td">{dash(l.car_no)}</td>
                      <td className="td text-xs text-ink-soft">{dash(l.owner_name)} · {dash(l.grp_name)}</td>
                      <td className="td text-right">
                        {canWrite ? (
                          <MoneyInput className="w-28 py-0.5" value={cellCost(l)} onChange={(v) => setCell(l, 'cost', v)} />
                        ) : money(l.cost_driver)}
                      </td>
                      <td className="td text-right">
                        {canWrite ? (
                          <input
                            className={`field num w-16 py-0.5 ${cellCnt(l) === '' ? 'border-red-300 bg-red-50' : ''}`}
                            inputMode="numeric"
                            value={cellCnt(l)}
                            placeholder="일수"
                            onChange={(e) => setCell(l, 'cnt', e.target.value.replace(/[^0-9]/g, ''))}
                          />
                        ) : dash(l.cost_driver_cnt)}
                      </td>
                      <td className={`td num ${edits[l.id] ? 'text-brand-700' : ''}`}>{money(String(amountOf(l)))}</td>
                      <td className="td whitespace-nowrap text-right">
                        {onReq && l.driver_id != null && (
                          <button className="btn-ghost mr-1 px-1.5 py-0 text-xs" disabled={busy}
                            title="이 기사에게 영업정산 추가지급·공제를 넣습니다"
                            onClick={() => {
                              if (dirty) { window.alert('저장하지 않은 일수·단가가 있습니다. 먼저 「일수·단가 저장」을 눌러 주세요.'); return; }
                              onReq(l);
                            }}>수당·공제</button>
                        )}
                        {canWrite && (
                          <button className="btn-ghost px-1.5 py-0 text-xs text-red-600" disabled={busy}
                            onClick={() => void act(() => api.removeCommuteLine({ id: l.id }))}>빼기</button>
                        )}
                      </td>
                    </tr>
                  )),
                  lines.length === 0 && !needle && (
                    <tr key={`e${r.dispt_id}`}>
                      <td className="td pl-6 text-xs text-ink-faint" colSpan={7}>
                        이 노선에 정산할 기사가 없습니다. 「기사 추가」로 넣거나, 이 달에 안 쓰는 노선이면
                        <button className="btn-ghost ml-1 px-1.5 py-0 text-xs text-red-600" disabled={busy}
                                onClick={() => { setRouteRemove(true); setRouteFor(r.dispt_id); }}>이 달 정산에서 빼기</button>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
            <tfoot className="border-t-2 border-line bg-canvas">
              <tr className="font-semibold">
                <td className="td" colSpan={4}>{needle ? `찾은 ${shownAll.length}줄 합계` : '합계'}</td>
                <td className="td num">{needle ? shownDays.toLocaleString() : ''}</td>
                <td className="td num">{money(String(needle ? shownTotal : total))}</td>
                <td className="td" />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {m && made.length === 0 && notMade.length === 0 && (
        <p className="text-sm text-ink-faint">이 접수에는 통근 노선이 없습니다.</p>
      )}
      {m && (
        <p className="break-keep text-xs text-ink-soft">
          일수가 빈 줄은 빨갛게 보입니다(지난달 값이 없는 새 배정). 저장하면 거래처별 정산 상세에 바로 반영되고, 기사 급여명세는 명세를 다시 만들면 들어갑니다.
          {m.limits.cntMax !== null && ` 일수는 ${m.limits.cntMax}일까지,`}
          {m.limits.costMin !== null && ` 단가는 ${m.limits.costMin.toLocaleString()}원 이상(또는 0)입니다.`}
        </p>
      )}

      {reloadOpen && (
        <Modal width="max-w-3xl">
          <CommuteReload recvId={recvId} driveMon={driveMon} onClose={() => setReloadOpen(false)}
            onDone={() => { setReloadOpen(false); reload(); }} />
        </Modal>
      )}
      {copyOpen && (
        <Modal width="max-w-xl">
          <CommuteCopy driveMon={driveMon} recvId={recvId} onClose={() => setCopyOpen(false)}
            onDone={() => { setCopyOpen(false); setEdits({}); reload(); }} />
        </Modal>
      )}
      {addFor !== null && m && (
        <Modal width="max-w-3xl">
          <AddDriver
            month={m}
            disptId={addFor}
            onClose={() => setAddFor(null)}
            onAdded={() => { setAddFor(null); reload(); }}
          />
        </Modal>
      )}
      {routeFor !== null && m && (
        <Modal>
          <RouteForm
            route={m.routes.find((r) => r.dispt_id === routeFor)!}
            driveMon={driveMon}
            startIncl={openRouteIncl === true && routeFor === openRoute}
            startRemove={routeRemove}
            onClose={() => { setRouteFor(null); setRouteRemove(false); }}
            onSaved={() => { setRouteFor(null); setRouteRemove(false); reload(); }}
          />
        </Modal>
      )}
    </div>
  );
}

/** 기사 추가 — 이 달에 걸친 배정 중 빠진 것(한 번에) 또는 편성 검색. */
/**
 * 새 노선 결재 올리기(2026-10-01 미팅 #30) — 지난달 정산에 없던 노선은 DYOPS 의사결정 결재를 받은 뒤에 정산 줄이 생긴다.
 * 결재선은 서버가 요청자의 DYOPS 기본 결재선으로 정한다. 결재가 끝나면 10분 안에(또는 「지금 확인」) 만들어진다.
 */
function RouteAsk({ recvId, driveMon, routes, onClose, onDone }: {
  recvId: number; driveMon: string; routes: CommuteMonth['routes']; onClose: () => void; onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ docId: string; approver1: string; approver2: string } | null>(null);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.requestCommuteRoutes({ recvId, driveMon, disptIds: routes.map((x) => x.dispt_id), reason });
      setDone(r);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '올리지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-3 p-5">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-bold">새 노선 결재 올리기 · {driveMon}</h2>
        <button className="btn-ghost ml-auto" data-modal-close onClick={done ? onDone : onClose}>닫기</button>
      </div>
      <p className="break-keep text-xs text-ink-soft">
        지난달 정산에 없던 노선입니다. 정산 부정을 막기 위해 팀장 결재(DYOPS 의사결정)를 받은 뒤에 정산 줄이 만들어집니다.
        결재선은 내 DYOPS 기본 결재선으로 정해집니다.
      </p>
      <ul className="list-disc space-y-0.5 pl-5 text-sm">
        {routes.map((r) => (
          <li key={r.dispt_id}>
            {dash(r.dispt_name)} <span className="text-xs text-ink-faint">이 달 배정 {r.cover_cnt}대 · 청구 {money(r.cost_cust)}</span>
          </li>
        ))}
      </ul>
      {done ? (
        <div className="space-y-2 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
          <p className="break-keep">
            결재를 올렸습니다(문서 {done.docId}). 결재자: 1차 {done.approver1} · 2차 {done.approver2}.
            최종 결재가 나면 10분 안에 정산 줄이 만들어집니다.
          </p>
          <button className="btn-primary py-1 text-xs" onClick={onDone}>확인</button>
        </div>
      ) : (
        <>
          <label className="block text-sm">
            <span className="text-ink-soft">사유 (왜 이 달에 새 노선이 생겼나요?)</span>
            <textarea className="field mt-1 h-24 w-full" value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)}
              placeholder="예: 거래처 요청으로 10/6부터 2호차 노선 신설(계약서 갱신분)" />
          </label>
          {error && <ErrorBox message={error} />}
          <div className="flex justify-end gap-2">
            <button className="btn-ghost" onClick={onClose}>취소</button>
            <button className="btn-primary" disabled={busy || reason.trim().length < 5} onClick={() => void send()}>
              {busy ? '올리는 중…' : '결재 올리기'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const REQ_TONE: Record<CommuteRouteRequest['status'], string> = {
  요청: 'bg-amber-100 text-amber-800', 승인: 'bg-sky-100 text-sky-800', 반영: 'bg-emerald-100 text-emerald-800',
  반려: 'bg-red-100 text-red-700', 취소: 'bg-gray-100 text-ink-soft',
};
const REQ_LABEL: Record<CommuteRouteRequest['status'], string> = {
  요청: '결재 대기', 승인: '승인 · 반영 대기', 반영: '반영됨', 반려: '반려', 취소: '취소',
};

/** 이 접수·달의 새 노선 결재 요청 — 상태 · 「지금 확인」(10분을 안 기다리고 결재 상태를 끌어온다) · 거두기. */
function RouteRequests({ recvId, driveMon, requests, canWrite, onChanged }: {
  recvId: number; driveMon: string; requests: CommuteRouteRequest[]; canWrite: boolean; onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const open = requests.some((r) => r.status === '요청' || r.status === '승인');

  async function check() {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const r = await api.checkCommuteRouteRequests({ recvId, driveMon });
      if (r.errors.length) setError(r.errors.join('\n'));
      setMsg(r.changed ? `${r.changed}건이 바뀌었습니다.` : '아직 결재가 끝나지 않았습니다.');
      if (r.changed) onChanged();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '확인하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }
  async function cancel(id: number) {
    if (!window.confirm('이 결재 요청을 거둘까요? DYOPS 의 결재 문서는 남으니 거기서도 회수해 주세요.')) return;
    setBusy(true);
    setError(null);
    try {
      await api.cancelCommuteRouteRequest({ requestId: id });
      onChanged();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '거두지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-line p-3 text-sm">
      <div className="mb-2 flex items-center gap-2">
        <b>새 노선 결재</b>
        <span className="text-xs text-ink-soft">결재가 끝나면 10분 안에 정산 줄이 만들어집니다.</span>
        {msg && <span className="text-xs text-ink-soft">{msg}</span>}
        {canWrite && open && (
          <button className="btn-ghost ml-auto py-1 text-xs" disabled={busy} onClick={() => void check()}>
            {busy ? '확인 중…' : '지금 확인'}
          </button>
        )}
      </div>
      {error && <ErrorBox message={error} />}
      <div className="space-y-1.5">
        {requests.map((r) => (
          <div key={r.request_id} className="flex flex-wrap items-start gap-2 border-t border-line pt-1.5 first:border-0 first:pt-0">
            <span className={`rounded px-1.5 text-xs ${REQ_TONE[r.status]}`}>{REQ_LABEL[r.status]}</span>
            <div className="min-w-0 flex-1 break-keep">
              <div>{dash(r.dispt_names)}</div>
              <div className="text-xs text-ink-faint">
                {r.created_at} {r.created_by.split('@')[0]} · 문서 {dash(r.draft_doc_id)} · 결재 {dash(r.approver1)} → {dash(r.approver2)}
                {r.status === '반영' && ` · 노선 ${r.applied_routes ?? 0} · 기사 ${r.applied_lines ?? 0}줄 만듦`}
              </div>
              <div className="text-xs text-ink-soft">사유: {r.reason}</div>
              {r.decided_note && <div className="text-xs text-red-700">{r.status === '반려' ? '반려 사유' : '메모'}: {r.decided_note}</div>}
              {r.check_error && <div className="text-xs text-amber-800">{r.check_error}</div>}
            </div>
            {canWrite && r.status === '요청' && (
              <button className="btn-ghost px-2 py-0.5 text-xs" disabled={busy} onClick={() => void cancel(r.request_id)}>거두기</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AddDriver({ month, disptId, onClose, onAdded }: { month: CommuteMonth; disptId: number; onClose: () => void; onAdded: () => void }) {
  const route = month.routes.find((r) => r.dispt_id === disptId);
  const cands = month.candidates.filter((c) => c.dispt_id === disptId);
  const [q, setQ] = useState('');
  const [allocs, setAllocs] = useState<CarAlloc[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setAllocs([]); return; }
    let alive = true;
    const h = setTimeout(() => {
      api.listCarAllocs({ q: t, limit: 30, running: true, started: true })
        .then((r) => alive && setAllocs(r.rows))
        .catch(() => alive && setAllocs([]));
    }, 300);
    return () => { alive = false; clearTimeout(h); };
  }, [q]);

  async function add(arg: { disptCarSchId?: number; carAllocId?: number }) {
    setBusy(true);
    setErr(null);
    try {
      await api.addCommuteLine({ disptId, driveMon: month.driveMon, ...arg });
      onAdded();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : '추가하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-3 p-4">
      <h2 className="text-lg font-bold">기사 추가 · {dash(route?.dispt_name)}</h2>
      {err && <ErrorBox message={err} />}
      {cands.length > 0 && (
        <div className="space-y-1 text-sm">
          <div className="text-ink-soft">이 달 배차된 기사 중 정산에 빠진 기사</div>
          {cands.map((c) => (
            <div key={c.dispt_car_sch_id} className="flex items-center gap-2">
              <span>{dash(c.driver_name)} · {dash(c.car_no)}</span>
              <span className="text-xs text-ink-faint">{dash(c.start_day)} ~ {dash(c.end_day)}</span>
              <button className="btn-ghost ml-auto py-0 text-xs" disabled={busy} onClick={() => void add({ disptCarSchId: c.dispt_car_sch_id })}>추가</button>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-1 text-sm">
        <div className="text-ink-soft">기사·차량번호로 찾기 (운행 중인 편성)</div>
        <input className="field w-full" placeholder="기사 이름 · 차량번호 · 차주 (2글자 이상)" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        <div className="max-h-72 overflow-y-auto">
          {allocs.map((a) => (
            <div key={a.car_alloc_id} className="flex items-center gap-2 border-b border-line py-1">
              <span>{dash(a.driver_name)} · {dash(a.car_no)}</span>
              <span className="text-xs text-ink-faint">{dash(a.owner_name)} · {dash(a.grp_name)}</span>
              <button className="btn-ghost ml-auto py-0 text-xs" disabled={busy} onClick={() => void add({ carAllocId: a.car_alloc_id })}>추가</button>
            </div>
          ))}
        </div>
      </div>
      <p className="text-xs text-ink-soft">단가는 노선 운행료 단가({money(route?.route_cost_driver)})로 들어가고, 일수는 표에서 적습니다.</p>
      <div className="flex justify-end">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>닫기</button>
      </div>
    </div>
  );
}

/** 노선의 이 달 청구 단가·횟수, 또는 이 달 정산에서 노선째 빼기. */
function RouteForm({ route, driveMon, startIncl, startRemove, onClose, onSaved }: {
  route: CommuteMonth['routes'][number]; driveMon: string; startIncl?: boolean; startRemove?: boolean; onClose: () => void; onSaved: () => void;
}) {
  // 부가세 포함(#247) — 포함가로 청구하는 노선이거나 「청구액(포함)」 칸에서 열면 포함 단가를 띄운다.
  //   포함으로 저장하면 서버가 공급가 = 포함 ÷ 1.1 로 맞춘다(1원 어긋남 없이 포함가 그대로 청구).
  const inclAtOpen = (startIncl === true && Number(route.cost_cust_add_tax ?? 0) > 0)
    || isVatIncl(route.cost_cust, route.cost_cust_add_tax);
  const [vatIncl, setVatIncl] = useState(inclAtOpen || startIncl === true);
  const [cust, setCust] = useState(moneyInput(inclAtOpen ? route.cost_cust_add_tax
    : startIncl ? String(Math.round(Number(route.cost_cust ?? 0) * 1.1)) : route.cost_cust));
  const [cnt, setCnt] = useState(String(route.cost_cust_cnt ?? 1));
  const [confirmRemove, setConfirmRemove] = useState(startRemove === true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run(arg: { costCust?: string; costCustCnt?: string; remove?: boolean; vatIncl?: boolean }) {
    setBusy(true);
    setErr(null);
    try {
      await api.saveCommuteRoute({ disptId: route.dispt_id, driveMon, ...arg });
      onSaved();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }
  const sum = Number(uncomma(cust) || 0) * Number(cnt || 0);

  return (
    <div className="card space-y-4 p-4">
      <div>
        <h2 className="text-lg font-bold">{dash(route.dispt_name)}</h2>
        <p className="mt-1 text-sm text-ink-soft">{driveMon} 청구 금액</p>
      </div>
      {err && <ErrorBox message={err} />}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <span className="mb-1 block text-sm text-ink-soft">청구 단가</span>
          <div className="mb-1"><VatToggle value={vatIncl} onChange={setVatIncl} disabled={busy} /></div>
          <MoneyInput value={cust} onChange={setCust} />
          <p className="mt-1"><VatPreview text={cust} incl={vatIncl} /></p>
        </div>
        <div>
          <span className="mb-1 block text-sm text-ink-soft">횟수</span>
          <input className="field num" inputMode="numeric" value={cnt} onChange={(e) => setCnt(e.target.value.replace(/[^0-9]/g, ''))} />
          <p className="mt-1 text-xs text-ink-faint">청구액{vatIncl ? '(포함)' : ''} {money(String(sum))}</p>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        {confirmRemove ? (
          <>
            <span className="mr-auto text-sm text-red-600">이 달 정산에서 노선과 기사 줄을 모두 뺍니다.</span>
            <button className="btn-ghost" disabled={busy} onClick={() => setConfirmRemove(false)}>취소</button>
            <button className="btn-primary bg-red-600" disabled={busy} onClick={() => void run({ remove: true })}>빼기</button>
          </>
        ) : (
          <>
            <button className="btn-ghost mr-auto text-red-600" disabled={busy} onClick={() => setConfirmRemove(true)}>이 달 정산에서 노선 빼기</button>
            <button className="btn-ghost" disabled={busy} onClick={onClose}>취소</button>
            <button className="btn-primary" disabled={busy} onClick={() => void run({ costCust: uncomma(cust), costCustCnt: cnt, vatIncl })}>저장</button>
          </>
        )}
      </div>
    </div>
  );
}

function prevMonOf(m: string): string {
  const y = Number(m.slice(0, 4));
  const mm = Number(m.slice(5, 7));
  return mm === 1 ? `${y - 1}-12` : `${y}-${String(mm - 1).padStart(2, '0')}`;
}

function ReloadTag({ on, tone, children }: { on: boolean; tone: 'amber' | 'red'; children: ReactNode }) {
  if (!on) return null;
  return (
    <span className={`rounded px-1.5 text-xs ${tone === 'red' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>{children}</span>
  );
}

/**
 * 「배차에서 다시 불러오기」(2026-09-30 way) — 이 달 줄과 지금 접수·배차의 차이를 보여 주고 고른 것만 반영한다.
 * 기본 체크: 새로 걸친 노선·배정은 넣기(단, 담당자가 이 달에 뺐던 것 · 노선 이름 날짜가 다른 달인 것은 빼 둔다) · 없어진 것은 빼기.
 */
export function CommuteReload({ recvId, driveMon, onClose, onDone }: {
  recvId: number; driveMon: string; onClose: () => void; onDone: () => void;
}) {
  const [d, setD] = useState<CommuteReloadDiff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [aR, setAR] = useState<Set<number>>(new Set());
  const [aL, setAL] = useState<Set<number>>(new Set());
  const [rL, setRL] = useState<Set<number>>(new Set());
  const [rR, setRR] = useState<Set<number>>(new Set());
  const [held, setHeld] = useState<CommuteHeld[] | null>(null);

  useEffect(() => {
    let alive = true;
    api.reloadCommuteRecv({ recvId, driveMon })
      .then((r) => {
        if (!alive) return;
        setD(r);
        const routes = new Set(r.addRoutes.filter((x) => !x.removed && !x.name_other).map((x) => x.dispt_id));
        setAR(routes);
        setAL(new Set(r.addLines.filter((x) => !x.removed && (!x.route_new || routes.has(x.dispt_id))).map((x) => x.dispt_car_sch_id)));
        setRL(new Set(r.removeLines.map((x) => x.id)));
        setRR(new Set(r.removeRoutes.map((x) => x.dispt_id)));
      })
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : '불러오지 못했습니다.'));
    return () => { alive = false; };
  }, [recvId, driveMon]);

  const flip = (set: (f: (s: Set<number>) => Set<number>) => void, id: number) =>
    set((cur) => {
      const n = new Set(cur);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  /** 새 노선의 기사는 그 노선을 고를 때만 들어간다(청구 줄이 먼저). */
  const lineOk = (id: number) => {
    const l = d?.addLines.find((x) => x.dispt_car_sch_id === id);
    return !!l && (!l.route_new || aR.has(l.dispt_id));
  };
  const pickedLines = [...aL].filter(lineOk);
  const total = aR.size + pickedLines.length + rL.size + rR.size;
  const empty = !!d && d.addRoutes.length + d.addLines.length + d.removeLines.length + d.removeRoutes.length === 0;

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.reloadCommuteRecv({
        recvId,
        driveMon,
        apply: { addRoutes: [...aR], addLines: pickedLines, removeLines: [...rL], removeRoutes: [...rR] },
      });
      // 새 노선 승인(미팅 #30) — 지난달에 없던 노선은 만들지 않았다. 알리고 닫기는 사람이 누른다.
      if (r.held?.length) setHeld(r.held);
      else onDone();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '반영하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-3 p-5">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-bold">배차에서 다시 불러오기 · {driveMon}</h2>
        <button className="btn-ghost ml-auto" data-modal-close onClick={onClose}>닫기</button>
      </div>
      <p className="break-keep text-xs text-ink-soft">
        지금 접수·배차의 배정 기간(출발~도착)과 이 달 정산 줄을 비교했습니다. 체크한 것만 반영합니다. 남는 줄의 단가·운행수는 그대로입니다.
      </p>
      {error && <ErrorBox message={error} />}
      {held && (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="break-keep">
            나머지는 반영했습니다. 지난달 정산에 없던 새 노선 {held.length}건({held.map((h) => h.dispt_name ?? `#${h.dispt_id}`).join(', ')})은
            결재가 필요해 만들지 않았습니다. 편집 창의 「결재 올리기」로 올려 주세요.
          </p>
          <button className="btn-primary py-1 text-xs" onClick={onDone}>확인</button>
        </div>
      )}
      {!d && !error && <p className="text-sm text-ink-faint">비교하는 중…</p>}
      {empty && <p className="text-sm text-ink-soft">배차와 차이가 없습니다.</p>}
      {d && d.closed.length > 0 && (
        <ErrorBox message={`${driveMon} ${d.closed.join('·')} 정산이 마감돼 반영할 수 없습니다. 마감을 푼 뒤 하세요.`} />
      )}

      <div className="max-h-[60vh] space-y-3 overflow-y-auto">
        {d && d.addRoutes.length > 0 && (
          <section className="space-y-1 text-sm">
            <b>넣을 노선 ({d.addRoutes.length})</b>
            {d.addRoutes.map((r) => (
              <label key={r.dispt_id} className="flex flex-wrap items-center gap-2">
                <input type="checkbox" checked={aR.has(r.dispt_id)} onChange={() => flip(setAR, r.dispt_id)} />
                <span>{dash(r.dispt_name)}</span>
                <span className="text-xs text-ink-faint">이 달 배정 {r.cover_cnt}대 · {r.made_prev ? '지난달 있음' : '지난달 없음'}</span>
                <ReloadTag on={r.removed} tone="amber">이 달에 뺐던 노선</ReloadTag>
                <ReloadTag on={r.name_other} tone="red">노선 이름 날짜가 다른 달</ReloadTag>
              </label>
            ))}
          </section>
        )}
        {d && d.addLines.length > 0 && (
          <section className="space-y-1 text-sm">
            <b>넣을 기사 ({d.addLines.length})</b>
            {d.addLines.map((l) => {
              const blocked = l.route_new && !aR.has(l.dispt_id);
              return (
                <label key={l.dispt_car_sch_id} className={`flex flex-wrap items-center gap-2 ${blocked ? 'opacity-40' : ''}`}>
                  <input
                    type="checkbox"
                    disabled={blocked}
                    checked={aL.has(l.dispt_car_sch_id) && !blocked}
                    onChange={() => flip(setAL, l.dispt_car_sch_id)}
                  />
                  <span>{dash(l.dispt_name)} · {dash(l.driver_name)} {dash(l.car_no)}</span>
                  <span className="text-xs text-ink-faint">배정 {dash(l.start_day)} ~ {dash(l.end_day)}</span>
                  <ReloadTag on={l.removed} tone="amber">이 달에 뺐던 기사</ReloadTag>
                  {blocked && <span className="text-xs text-ink-faint">노선을 먼저 고르세요</span>}
                </label>
              );
            })}
          </section>
        )}
        {d && d.removeLines.length > 0 && (
          <section className="space-y-1 text-sm">
            <b>뺄 기사 ({d.removeLines.length})</b>
            {d.removeLines.map((l) => (
              <label key={l.id} className="flex flex-wrap items-center gap-2">
                <input type="checkbox" checked={rL.has(l.id)} onChange={() => flip(setRL, l.id)} />
                <span>{dash(l.dispt_name)} · {dash(l.driver_name)} {dash(l.car_no)}</span>
                <span className="text-xs text-ink-faint">{l.reason}</span>
              </label>
            ))}
          </section>
        )}
        {d && d.removeRoutes.length > 0 && (
          <section className="space-y-1 text-sm">
            <b>뺄 노선 ({d.removeRoutes.length})</b>
            {d.removeRoutes.map((r) => (
              <label key={r.dispt_id} className="flex flex-wrap items-center gap-2">
                <input type="checkbox" checked={rR.has(r.dispt_id)} onChange={() => flip(setRR, r.dispt_id)} />
                <span>{dash(r.dispt_name)}</span>
                <span className="text-xs text-ink-faint">{r.reason} · 기사 {r.line_cnt}줄도 같이 빠짐</span>
              </label>
            ))}
          </section>
        )}
      </div>
      {d && !empty && (
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>취소</button>
          <button className="btn-primary" disabled={busy || !!held || total === 0 || d.closed.length > 0} onClick={() => void run()}>
            {busy ? '반영 중…' : `고른 ${total}건 반영`}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * 「다른 달 그대로 가져오기」 — 고른 달의 노선·기사·단가·운행수를 이 달로 복사(접수·배차와 무관).
 * `recvId` 가 없으면 담당 범위 안 통근 접수 전부. 이 달에 있던 줄은 지우고 바꾼다 — 건수를 먼저 보여 준다.
 */
export function CommuteCopy({ driveMon, recvId, onClose, onDone }: {
  driveMon: string; recvId?: number; onClose: () => void; onDone: () => void;
}) {
  const [from, setFrom] = useState(prevMonOf(driveMon));
  const [pv, setPv] = useState<{ recvs: number; from: { routes: number; lines: number }; cur: { routes: number; lines: number } } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [held, setHeld] = useState<CommuteHeld[] | null>(null);

  useEffect(() => {
    setPv(null);
    setError(null);
    if (!/^\d{4}-\d{2}$/.test(from) || from === driveMon) return;
    let alive = true;
    api.copyCommuteMonth({ driveMon, fromMon: from, recvId, dryRun: true })
      .then((r) => { if (alive) setPv(r); })
      .catch((e: unknown) => { if (alive) setError(e instanceof Error ? e.message : '불러오지 못했습니다.'); });
    return () => { alive = false; };
  }, [driveMon, from, recvId]);

  async function run() {
    if (!pv) return;
    const msg = `${driveMon} 통근 줄(노선 ${pv.cur.routes} · 기사 ${pv.cur.lines}줄)을 지우고 ${from} 것(노선 ${pv.from.routes} · 기사 ${pv.from.lines}줄)으로 바꿉니다. 진행할까요?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.copyCommuteMonth({ driveMon, fromMon: from, recvId });
      // 새 노선 승인(미팅 #30) — 지난달에 없던 노선은 안 옮겼다.
      if (r.held?.length) setHeld(r.held);
      else onDone();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '가져오지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-3 p-5">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-bold">다른 달 그대로 가져오기 · {driveMon}{recvId ? '' : ' · 통근 전체'}</h2>
        <button className="btn-ghost ml-auto" data-modal-close onClick={onClose}>닫기</button>
      </div>
      <p className="break-keep text-xs text-ink-soft">
        고른 달의 노선·기사·단가·운행수를 접수·배차와 상관없이 그대로 이 달로 옮깁니다.
        이 달에 이미 있던 {recvId ? '이 접수의 ' : ''}통근 줄은 지우고 바꿉니다.
      </p>
      <div className="flex items-center gap-2 text-sm">
        <span>가져올 달</span>
        <input className="field w-40" type="month" value={from} onChange={(e) => setFrom(e.target.value)} />
      </div>
      {from === driveMon && <p className="text-sm text-red-600">이 달과 다른 달을 고르세요.</p>}
      {error && <ErrorBox message={error} />}
      {held && (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="break-keep">
            가져왔습니다. 다만 지난달 정산에 없던 노선 {held.length}건({held.map((h) => h.dispt_name ?? `#${h.dispt_id}`).join(', ')})은
            결재가 필요해 옮기지 않았습니다. 접수 편집 창의 「결재 올리기」로 올려 주세요.
          </p>
          <button className="btn-primary py-1 text-xs" onClick={onDone}>확인</button>
        </div>
      )}
      {pv && (
        <p className="text-sm">
          {recvId ? '' : `접수 ${pv.recvs}개 · `}{from}: 노선 <b>{pv.from.routes}</b> · 기사 <b>{pv.from.lines}</b>줄
          <span className="mx-2 text-ink-faint">→</span>
          {driveMon} 지금 노선 {pv.cur.routes} · 기사 {pv.cur.lines}줄은 지워짐
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>취소</button>
        <button className="btn-primary" disabled={busy || !!held || !pv || pv.from.routes === 0} onClick={() => void run()}>
          {busy ? '가져오는 중…' : '그대로 가져오기'}
        </button>
      </div>
    </div>
  );
}

/**
 * 거래처별 정산 탭 위 통근 안내줄 — 자동 반영 설명 + 이 달 줄이 없는 통근 접수 + 통근 전체 「다른 달 그대로 가져오기」.
 *
 * 🔴 거래처별 정산 목록은 **이 달 정산 줄이 있는 접수만** 보인다 — 줄이 없는 통근 접수는 검색해도 안 나오고
 * 「노선·기사 편집」을 열 길이 없었다(게시판 #93 · 9월 16580 NH농협: 8월까지 줄이 있었는데 9월 0).
 * 9월까지는 자동 반영이 없어(AUTO_FROM) 이런 접수가 32곳이었다. 여기서 목록으로 보여 주고 편집기를 바로 연다.
 * `ready` = 목록 로딩이 끝났다(10월~ 자동 반영이 목록 조회 때 돌므로 그 뒤에 세야 수가 맞다).
 */
export function CommuteMonthBar({ driveMon, q, ready, nonce, onChanged }: {
  driveMon: string; q?: string; ready: boolean; nonce: number; onChanged: () => void;
}) {
  const { me } = useMe();
  const canWrite = canScreen(me, ['settle']);
  const [copy, setCopy] = useState(false);
  const [ov, setOv] = useState<CommuteOverview | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [openRecv, setOpenRecv] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const valid = /^\d{4}-\d{2}$/.test(driveMon);

  useEffect(() => {
    if (!valid || !ready) return;
    let alive = true;
    api.commuteMonthOverview({ driveMon })
      .then((r) => { if (alive) setOv(r); })
      .catch(() => { if (alive) setOv(null); });
    return () => { alive = false; };
  }, [driveMon, ready, nonce, tick, valid]);
  useEffect(() => { setOv(null); setListOpen(false); setOpenRecv(null); }, [driveMon]);

  /** 이 달 줄이 하나도 없는 통근 접수. 지난달에 정산했던 곳이 위로. */
  const missing = useMemo(
    () => (ov?.driveMon === driveMon ? ov.rows : [])
      .filter((r) => r.made_cnt === 0 && (r.prev_missing > 0 || r.cover_missing > 0))
      .sort((a, b) => Number(b.prev_missing > 0) - Number(a.prev_missing > 0)),
    [ov, driveMon],
  );
  const prevCnt = missing.filter((r) => r.prev_missing > 0).length;
  /** 검색어로 찾은 접수가 이 달 줄이 없는 통근이면 바로 알려 준다(목록엔 안 나오므로). */
  const searched = useMemo(() => {
    const words = (q ?? '').trim().toLowerCase().split(' ').filter(Boolean);
    if (!words.length) return [];
    return missing.filter((r) => {
      const s = `${r.cust_name ?? ''} ${r.recv_id}`.toLowerCase();
      return words.every((w) => s.includes(w));
    }).slice(0, 5);
  }, [missing, q]);

  if (!valid) return null;
  return (
    <div className="space-y-1">
      {/* 한 줄 띠(2026-09-30 way 「위칸을 최대로 좁히고」) — 설명은 한 줄로 자르고 전체는 툴팁. */}
      {/* PC 는 한 줄 고정 — 좁은 자리에서 버튼 글자가 세 줄로 접혀 띠가 96px 이 됐다(10-29 1280 폭 실측). 자리가 모자라면 띠째 마감 줄 아래로 내려간다(Settle.tsx md:min-w). */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/50 px-3 py-1.5 text-xs text-ink-soft md:flex-nowrap">
        <span className="rounded bg-violet-100 px-1.5 py-0.5 font-medium text-violet-700">통근</span>
        <span className="min-w-0 flex-1 cursor-help truncate" title={driveMon >= '2026-10'
          ? '배정 기간(출발~도착)이 이 달에 걸친 통근 노선이 자동으로 들어옵니다(지난달 정산에 있었거나 새로 배정된 노선). 운행수는 지난달 값으로 채워지고, 없으면 빨간 빈칸입니다 — 거래처 줄을 펼쳐 바로 고치세요.'
          : '9월까지의 통근 줄은 버스인에서 만든 그대로입니다. 거래처 줄을 펼쳐 고칠 수 있습니다.'}>
          {driveMon >= '2026-10'
            ? '배정 기간(출발~도착)이 이 달에 걸친 통근 노선이 자동으로 들어옵니다(지난달 정산에 있었거나 새로 배정된 노선). 운행수는 지난달 값으로 채워지고, 없으면 빨간 빈칸입니다 — 거래처 줄을 펼쳐 바로 고치세요.'
            : '9월까지의 통근 줄은 버스인에서 만든 그대로입니다. 거래처 줄을 펼쳐 고칠 수 있습니다.'}
        </span>
        <div className="ml-auto flex shrink-0 items-center gap-2 whitespace-nowrap">
          {missing.length > 0 && (
            <button className={`btn-ghost py-0.5 text-xs ${prevCnt > 0 ? 'font-semibold text-amber-700' : ''}`} onClick={() => setListOpen(true)}>
              이 달 정산 줄이 없는 통근 접수 {prevCnt > 0 ? prevCnt : missing.length}곳
            </button>
          )}
          {canWrite && (
            <button className="btn-ghost py-0.5 text-xs" onClick={() => setCopy(true)}>다른 달 그대로 가져오기(통근 전체)</button>
          )}
        </div>
      </div>
      {searched.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <span className="break-keep">찾으신 통근 접수는 {driveMon} 정산 줄이 아직 없어 아래 목록에 안 나옵니다.</span>
          {searched.map((r) => (
            <button key={r.recv_id} className="btn-ghost py-0.5 text-xs" onClick={() => setOpenRecv(r.recv_id)}>
              {dash(r.cust_name)} · NO {r.recv_id} 노선·기사 편집
            </button>
          ))}
        </div>
      )}
      {listOpen && openRecv === null && (
        <Modal width="max-w-3xl">
          <CommuteMissingList driveMon={driveMon} prevMon={ov?.prevMon} rows={missing}
            onPick={(id) => setOpenRecv(id)} onClose={() => setListOpen(false)} />
        </Modal>
      )}
      {openRecv !== null && (
        <Modal width="max-w-6xl">
          <CommuteEditor
            recvId={openRecv}
            driveMon={driveMon}
            canWrite={canWrite}
            onClose={() => { setOpenRecv(null); setTick((n) => n + 1); onChanged(); }}
          />
        </Modal>
      )}
      {copy && (
        <Modal width="max-w-xl">
          <CommuteCopy driveMon={driveMon} onClose={() => setCopy(false)} onDone={() => { setCopy(false); setTick((n) => n + 1); onChanged(); }} />
        </Modal>
      )}
    </div>
  );
}

/** 이 달 정산 줄이 없는 통근 접수 목록 — 줄을 누르면 그 접수의 노선·기사 편집(노선 만들기)이 열린다. */
function CommuteMissingList({ driveMon, prevMon, rows, onPick, onClose }: {
  driveMon: string; prevMon: string | undefined; rows: CommuteOverview['rows'];
  onPick: (recvId: number) => void; onClose: () => void;
}) {
  const [onlyPrev, setOnlyPrev] = useState(rows.some((r) => r.prev_missing > 0));
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const words = q.trim().toLowerCase().split(' ').filter(Boolean);
    return rows.filter((r) => (!onlyPrev || r.prev_missing > 0)
      && words.every((w) => `${r.cust_name ?? ''} ${r.recv_id}`.toLowerCase().includes(w)));
  }, [rows, onlyPrev, q]);
  return (
    <div className="card space-y-3 p-5">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-bold">{driveMon} 정산 줄이 없는 통근 접수</h2>
        <button className="btn-ghost ml-auto" data-modal-close onClick={onClose}>닫기</button>
      </div>
      <p className="break-keep text-xs text-ink-soft">
        접수를 누르면 노선·기사 편집이 열립니다. 지난달에 정산한 노선이 미리 체크되어 있으니 노선을 만든 뒤 운행수를 적으시면 됩니다.
      </p>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={onlyPrev} onChange={(e) => setOnlyPrev(e.target.checked)} />
          지난달({prevMon})에 정산한 접수만
        </label>
        <input className="field ml-auto w-48" placeholder="거래처 · 접수 NO" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="max-h-[60vh] overflow-auto rounded-lg border border-line">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="th text-right">접수 NO</th>
              <th className="th">거래처</th>
              <th className="th text-right" title="지난달엔 정산했는데 이 달엔 아직 없는 노선">지난달 노선</th>
              <th className="th text-right" title="배정 기간이 이 달에 걸친 노선">배정 걸친 노선</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.recv_id} className="cursor-pointer hover:bg-canvas" onClick={() => onPick(r.recv_id)}>
                <td className="td num text-xs text-ink-soft">{r.recv_id}</td>
                <td className="td">{dash(r.cust_name)}</td>
                <td className={`td num ${r.prev_missing > 0 ? 'font-semibold text-amber-700' : 'text-ink-faint'}`}>{r.prev_missing || '—'}</td>
                <td className="td num">{r.cover_missing || '—'}</td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td className="td text-ink-faint" colSpan={4}>해당하는 접수가 없습니다.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
